import { Directive, ElementRef, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StableTextService } from './stable-text.service';

/**
 * Renders buffered, word-by-word typed text for a single message.
 * Subscribes to StableTextService's display stream and pending work stream
 * to coordinate sequential message display in the subtitle box.
 */
@Directive({
  selector: '[appStableText]',
  standalone: true
})
export class StableTextDirective implements OnInit, OnDestroy {
  readonly documentId = input.required<string>();
  readonly isFinal = input(false);

  readonly typingStateChange = output<boolean>();
  readonly typingComplete = output<void>();

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly stableTextService = inject(StableTextService);
  private readonly destroy$ = new Subject<void>();
  private lastBehindState = false;

  ngOnInit(): void {
    const docId = this.documentId();

    this.stableTextService.getDisplayStream(docId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(displayedText => {
        this.el.nativeElement.textContent = displayedText;
        this.el.nativeElement.classList.toggle('has-content', displayedText.length > 0);
      });

    this.stableTextService.getPendingWorkStream(docId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasPending => {
        if (hasPending !== this.lastBehindState) {
          this.lastBehindState = hasPending;
          this.typingStateChange.emit(hasPending);
          if (!hasPending) this.typingComplete.emit();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stableTextService.removeMessage(this.documentId());
  }
}
